'use client';

/**
 * SearchTabs Component
 *
 * Horizontal tab bar for search results page.
 * Shows tabs with result counts.
 *
 * Supports TWO modes:
 * - Client-side switching (when onTabChange is provided) - no page navigation
 * - Link-based navigation (when onTabChange is NOT provided) - for fallback/SEO
 */

'use client';

import { BRAND } from '@/lib/cms/config';
import { Link } from '@/lib/i18n/navigation';
import { searchPaths, type SearchTab } from '@/lib/paths';
import type { SearchCounts } from '@/lib/search';
import clsx from 'clsx';
import { useAriaLabels } from '@/hooks/useAriaLabels';
import { useTranslations } from 'next-intl';
import styles from './SearchTabs.module.scss';

export interface SearchTabsProps {
  /** Currently active tab */
  activeTab: SearchTab;
  /** Search query to preserve in tab links */
  query: string;
  /** Result counts for each tab */
  counts: SearchCounts;
  /** Callback for client-side tab switching (when provided, uses buttons instead of links) */
  onTabChange?: (tab: SearchTab) => void;
  /** Additional class name */
  className?: string;
}

// Gallet has no eyewear catalogue, so the Eyewear tab is hard-removed for it.
const TABS: SearchTab[] =
  BRAND === 'gallet'
    ? ['all', 'watches', 'straps', 'stores']
    : ['all', 'watches', 'straps', 'eyewear', 'stores'];

export function SearchTabs({ activeTab, query, counts, onTabChange, className }: SearchTabsProps) {
  const t = useTranslations('search.tabs');
  const aria = useAriaLabels();

  const getTabLabel = (tab: SearchTab): string => {
    const labels: Record<SearchTab, string> = {
      all: t('all'),
      watches: t('watches'),
      straps: t('straps'),
      eyewear: t('eyewear'),
      stores: t('stores'),
    };
    return labels[tab];
  };

  const getTabCount = (tab: SearchTab): number => {
    return counts[tab] || 0;
  };

  const getTabUrl = (tab: SearchTab): string => {
    return searchPaths.forTab(tab, query);
  };

  return (
    <nav
      className={clsx(styles.tabs, className)}
      role="tablist"
      aria-label={aria.action('searchResultsTabs')}
    >
      {TABS.map((tab, index) => {
        const isActive = tab === activeTab;
        const count = getTabCount(tab);
        const isFirst = index === 0;

        // Client-side tab switching (no page navigation)
        if (onTabChange) {
          return (
            <div key={tab} className={styles.tabWrapper}>
              {!isFirst && (
                <span className={styles.separator} aria-hidden="true">
                  |
                </span>
              )}
              <button
                type="button"
                onClick={() => onTabChange(tab)}
                className={clsx(styles.tab, {
                  [styles.active]: isActive,
                  [styles.empty]: count === 0,
                })}
                role="tab"
                aria-selected={isActive}
                aria-controls={`tabpanel-${tab}`}
              >
                <span className={styles.label}>{getTabLabel(tab)}</span>
                <span className={styles.count} aria-label={`${count} results`}>
                  ({count})
                </span>
              </button>
            </div>
          );
        }

        // Link-based navigation (fallback/SEO)
        return (
          <div key={tab} className={styles.tabWrapper}>
            {!isFirst && (
              <span className={styles.separator} aria-hidden="true">
                |
              </span>
            )}
            <Link
              href={getTabUrl(tab)}
              className={clsx(styles.tab, {
                [styles.active]: isActive,
                [styles.empty]: count === 0,
              })}
              role="tab"
              aria-selected={isActive}
              aria-controls={`tabpanel-${tab}`}
              scroll={false}
            >
              <span className={styles.label}>{getTabLabel(tab)}</span>
              <span className={styles.count} aria-label={`${count} results`}>
                ({count})
              </span>
            </Link>
          </div>
        );
      })}
    </nav>
  );
}

SearchTabs.displayName = 'SearchTabs';
